import React from 'react';
import LegalPageLayout, { LegalSection } from './LegalPageLayout';

const sections: LegalSection[] = [
  {
    title: '1. Scope of these terms',
    body: [
      'These Terms and Conditions apply to your access to and use of Dinelyt, including our website, restaurant onboarding flows, QR-based dine-in ordering journeys, restaurant dashboards, billing tools, communication features, and related support services.',
      'They apply to both restaurant partners using Dinelyt to manage operations and diners who interact with Dinelyt-powered table ordering, menu browsing, payment, or feedback experiences.',
    ],
  },
  {
    title: '2. About the Dinelyt service',
    body: [
      'Dinelyt is a digital dine-in platform built to help restaurants manage table-based ordering, menus, staff workflows, billing, and customer engagement from a single system. Depending on the plan and setup, the platform may include restaurant onboarding, QR-enabled ordering, role-based staff access, analytics, file uploads, invoice workflows, and operational notifications.',
      'We continuously refine the platform to improve reliability, speed, usability, and fit for hospitality businesses. Because of this, features may evolve over time and some features may be added, changed, or retired.',
    ],
  },
  {
    title: '3. Restaurant partner responsibilities',
    body: [
      'If you register a restaurant or manage a Dinelyt account for a business, you are responsible for ensuring the information you provide is accurate, complete, and kept up to date. This includes contact information, brand assets, menus, pricing, tax details, payment instructions, operating hours, and any regulatory or business information shown to diners or staff.',
      'Restaurant partners are also responsible for managing user access appropriately, protecting login credentials, and ensuring that only authorized team members are allowed to access owner, manager, or staff dashboards.',
    ],
  },
  {
    title: '4. Diner use of the platform',
    body: [
      'Diners may access Dinelyt through QR codes, links, or restaurant-provided flows to browse menus, create dine-in sessions, place orders, view order status, and in some cases initiate payments or receive invoices. By using these flows, diners agree to provide accurate information where requested and to use the platform only for legitimate dining-related purposes.',
      'Diners must not attempt to misuse table sessions, impersonate another guest, interfere with ordering flows, or use automated means to disrupt restaurant operations.',
    ],
  },
  {
    title: '5. Orders, menus and pricing',
    body: [
      'Restaurants control their own menus, pricing, taxes, availability, operating hours, and fulfillment decisions. Dinelyt provides the digital interface and operational tooling, but menu descriptions, stock availability, pricing, allergens, taxes, and final fulfillment remain the responsibility of the restaurant.',
      'If an item is unavailable, incorrectly listed, or cannot be fulfilled for operational reasons, the restaurant may update, reject, or revise the order according to its own service policies and applicable law.',
    ],
  },
  {
    title: '6. Payments and billing',
    body: [
      'Dinelyt may support billing and payment-related workflows, including bill generation, invoice sharing, payment references, and operational tracking. However, the restaurant remains responsible for the correctness of charges, taxes, discounts, payment instructions, and statutory compliance connected with each transaction.',
      'Where third-party payment rails or bank-based methods are used, those payment channels may be governed by additional terms from the relevant provider or financial institution.',
    ],
  },
  {
    title: '7. Account security and access control',
    body: [
      'You are responsible for maintaining the confidentiality of your credentials and for activity carried out under your account. If you believe your account has been accessed without authorization, you should notify Dinelyt promptly so that we can help secure the environment.',
      'We may suspend, restrict, or terminate access where we reasonably believe there has been misuse, fraud, unauthorized access, or conduct that creates risk for diners, restaurant operations, or the platform.',
    ],
  },
  {
    title: '8. Acceptable use',
    body: [
      'You must not use Dinelyt to upload unlawful content, infringe intellectual property, distribute malware, reverse engineer critical portions of the service, interfere with platform performance, scrape data without permission, or attempt to bypass security, role restrictions, or access controls.',
      'Restaurants must also ensure that content uploaded to Dinelyt, including logos, menu images, item descriptions, and business documents, is lawful and that they have the right to use it.',
    ],
  },
  {
    title: '9. Intellectual property',
    body: [
      'The Dinelyt brand, website, software, platform design, workflows, and associated content created by us remain our intellectual property or that of our licensors. These terms do not transfer any ownership rights to you.',
      'Restaurant-provided content such as logos, menus, and business information remains the property of the restaurant or the relevant rights holder, but you grant Dinelyt the right to host, display, process, and use that content as needed to operate the service.',
    ],
  },
  {
    title: '10. Availability and support',
    body: [
      'We aim to keep Dinelyt available and reliable, but uninterrupted service cannot be guaranteed. Maintenance, updates, third-party outages, internet disruptions, and other operational issues may affect availability from time to time.',
      'We may provide onboarding, support, and issue resolution assistance through the channels made available on our website or through business communications, but response times may vary depending on the nature of the issue and the partner relationship.',
    ],
  },
  {
    title: '11. Limitation of liability',
    body: [
      'To the maximum extent permitted by law, Dinelyt will not be liable for indirect, incidental, special, consequential, or business interruption losses arising from the use of or inability to use the platform, including loss of revenue, loss of business opportunity, or operational disruption.',
      'Our total liability in connection with the service will be limited to the amount paid by the relevant restaurant partner to Dinelyt for the affected service period, or a reasonable nominal amount where the user is a diner using a free ordering interface.',
    ],
  },
  {
    title: '12. Termination and updates',
    body: [
      'We may update these Terms and Conditions from time to time to reflect legal, operational, or product changes. Updated terms will become effective when posted on the Dinelyt website unless a different date is stated.',
      'Continued use of Dinelyt after an update means you accept the revised terms. If you do not agree with an update, you should stop using the service.',
    ],
  },
  {
    title: '13. Contact',
    body: [
      'Questions regarding these Terms and Conditions can be sent to info@dinelyt.com. If you are a restaurant partner and your issue relates to billing, onboarding, account access, or a customer complaint, include your restaurant name and registered contact details so we can assist faster.',
    ],
  },
];

const TermsAndConditionsPage: React.FC = () => (
  <LegalPageLayout
    eyebrow="Legal Terms"
    title="Terms & Conditions"
    intro="These terms explain how Dinelyt may be used by restaurant partners, staff members, and diners who interact with our digital dine-in platform. They are designed to be practical, hospitality-focused, and aligned with the way Dinelyt is used in real restaurant operations."
    effectiveDate="April 19, 2026"
    sections={sections}
  />
);

export default TermsAndConditionsPage;
